// src/pages/Dashboard.js
import React, { useEffect, useState } from 'react';
import { getSummary, getPipeline, getOrders } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { T, fmt, Badge, Card, StatCard, Spinner, TH, TD, STATUS_COLOR } from '../components/UI';

export default function Dashboard() {
  const { user, can } = useAuth();
  const [summary, setSummary]   = useState(null);
  const [pipeline, setPipeline] = useState([]);
  const [orders, setOrders]     = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    Promise.all([getSummary(), getPipeline(), getOrders()])
      .then(([s, p, o]) => {
        setSummary(s.data.data);
        setPipeline(p.data.data);
        setOrders(o.data.data.slice(0, 6));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;

  const s = summary || {};

  return (
    <div>
      <div style={{ marginBottom:'22px' }}>
        <div style={{ fontSize:'22px', fontWeight:800, color:T.text }}>Welcome, {user?.name} 👋</div>
        <div style={{ fontSize:'13px', color:T.muted, marginTop:'4px', textTransform:'capitalize' }}>{user?.role?.replace('_',' ')} Dashboard</div>
      </div>

      {/* Alert banner for management */}
      {can('approve_printing') && s.needApproval > 0 && (
        <div style={{ background:`${T.danger}18`, border:`1px solid ${T.danger}44`, borderRadius:'10px', padding:'14px 18px', marginBottom:'20px', display:'flex', alignItems:'center', gap:'12px' }}>
          <span style={{ fontSize:'20px' }}>🔴</span>
          <div>
            <div style={{ fontWeight:700, color:T.danger }}>{s.needApproval} order{s.needApproval > 1 ? 's' : ''} awaiting your printing approval!</div>
            <div style={{ fontSize:'12px', color:T.muted, marginTop:'2px' }}>Go to the Printing tab to review and approve.</div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div style={{ display:'flex', gap:'14px', flexWrap:'wrap', marginBottom:'20px' }}>
        <StatCard label="Total Orders"   value={s.totalOrders || 0} />
        <StatCard label="Total Collected" value={fmt(s.totalCollected)} color={T.gold} />
        <StatCard label="Pending Amount"  value={fmt(s.pendingAmount)}  color={T.warning} />
        <StatCard label="Delivered"       value={s.deliveredCount || 0} color={T.success} />
        <StatCard label="Need Approval"   value={s.needApproval || 0}   color={s.needApproval > 0 ? T.danger : T.muted} />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px', marginBottom:'16px' }}>
        {/* Finance Split */}
        <Card>
          <div style={{ fontSize:'13px', fontWeight:700, color:T.gold, marginBottom:'14px' }}>💰 Finance Split</div>
          <div style={{ display:'flex', gap:'10px', marginBottom:'12px' }}>
            <div style={{ flex:1, background:`${T.success}11`, border:`1px solid ${T.success}33`, borderRadius:'8px', padding:'14px', textAlign:'center' }}>
              <div style={{ fontSize:'10px', color:T.muted, marginBottom:'4px' }}>
                BUSINESS FUND{s.totalCollected > 0 ? ` ${Math.round((s.businessFund / s.totalCollected) * 100)}%` : ''}
              </div>
              <div style={{ fontSize:'18px', fontWeight:800, color:T.success }}>{fmt(s.businessFund)}</div>
            </div>
            <div style={{ flex:1, background:`${T.gold}11`, border:`1px solid ${T.gold}33`, borderRadius:'8px', padding:'14px', textAlign:'center' }}>
              <div style={{ fontSize:'10px', color:T.muted, marginBottom:'4px' }}>
                PROFIT POOL{s.totalCollected > 0 ? ` ${Math.round((s.profitPool / s.totalCollected) * 100)}%` : ''}
              </div>
              <div style={{ fontSize:'18px', fontWeight:800, color:T.gold }}>{fmt(s.profitPool)}</div>
            </div>
          </div>
          <div style={{ fontSize:'12px', color:T.muted, borderTop:`1px solid ${T.border}`, paddingTop:'10px' }}>
            Per Member Share (÷{s.profitPoolMembers || 5}): <span style={{ color:'#5be3ff', fontWeight:700 }}>{fmt(s.perMember)}</span>
          </div>
        </Card>

        {/* Pipeline */}
        <Card>
          <div style={{ fontSize:'13px', fontWeight:700, color:T.gold, marginBottom:'12px' }}>📊 Order Pipeline</div>
          {pipeline.length === 0
            ? <div style={{ fontSize:'12px', color:T.muted }}>No orders yet.</div>
            : pipeline.map(p => (
              <div key={p.status} style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'7px' }}>
                <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:STATUS_COLOR[p.status]||T.muted, flexShrink:0 }} />
                <div style={{ flex:1, fontSize:'12px', color:T.muted }}>{p.status.replace(/_/g,' ')}</div>
                <div style={{ fontSize:'12px', fontWeight:700 }}>{p.count}</div>
              </div>
            ))}
        </Card>
      </div>

      {/* Recent Orders */}
      <Card>
        <div style={{ fontSize:'13px', fontWeight:700, color:T.gold, marginBottom:'14px' }}>📦 Recent Orders</div>
        {orders.length === 0
          ? <div style={{ fontSize:'13px', color:T.muted, textAlign:'center', padding:'20px' }}>No orders yet.</div>
          : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>
                <th style={TH}>Order ID</th><th style={TH}>Customer</th><th style={TH}>Amount</th><th style={TH}>Status</th><th style={TH}>Date</th>
              </tr></thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id}>
                    <td style={{ ...TD, color:T.gold, fontWeight:700 }}>{o.order_number}</td>
                    <td style={TD}>{o.customer_name}</td>
                    <td style={{ ...TD, fontWeight:600 }}>{fmt(o.total_amount)}</td>
                    <td style={TD}><Badge status={o.status} /></td>
                    <td style={{ ...TD, color:T.muted, fontSize:'12px' }}>{new Date(o.created_at).toLocaleDateString('en-KE')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </Card>
    </div>
  );
}
